/**
 * Hyperliquid Builder Fee Approval
 *
 * Handles the one-time EIP-712 signature that approves a builder to
 * receive fees on trades. Builder address and fee rate are parameterized
 * (not hardcoded) — each tenant configures their own builder.
 *
 * Flow:
 * 1. User's main wallet signs the ApproveBuilderFee EIP-712 message
 * 2. Signed approval is submitted to HL /exchange
 * 3. Builder can now include fee in order actions
 *
 * Ported from perpetratr/packages/hyperliquid/src/approval.ts.
 */

import { HL_EIP712_DOMAIN, HL_MAINNET_API, HL_TESTNET_API } from "./constants";
import { splitSignature } from "./signer";
import type { HLBuilderConfig, HLSignature } from "./types";

// ============================================================================
// EIP-712 Typed Data
// ============================================================================

/**
 * Build the EIP-712 typed data for builder fee approval.
 * The main wallet signs this to authorize the builder to collect fees.
 */
export function getBuilderApprovalTypedData(
  builder: HLBuilderConfig,
  nonce: number,
  isMainnet = true,
) {
  return {
    domain: HL_EIP712_DOMAIN,
    types: {
      "HyperliquidTransaction:ApproveBuilderFee": [
        { name: "hyperliquidChain", type: "string" },
        { name: "maxFeeRate", type: "string" },
        { name: "builder", type: "address" },
        { name: "nonce", type: "uint64" },
      ],
    },
    primaryType: "HyperliquidTransaction:ApproveBuilderFee" as const,
    message: {
      hyperliquidChain: isMainnet ? "Mainnet" : "Testnet",
      maxFeeRate: `${(builder.feeRate / 100).toFixed(1)}%`, // 10 bps → "0.1%"
      builder: builder.address as `0x${string}`,
      nonce: BigInt(nonce),
    },
  };
}

// ============================================================================
// Submission
// ============================================================================

/**
 * Submit a signed builder fee approval to Hyperliquid.
 *
 * @param signature - Raw EIP-712 signature from wallet
 * @param builder - Builder config (address + fee rate)
 * @param nonce - The nonce used when signing
 * @param isMainnet - Whether to submit to mainnet or testnet
 */
export async function submitBuilderApproval(
  signature: `0x${string}`,
  builder: HLBuilderConfig,
  nonce: number,
  isMainnet = true,
): Promise<{ success: boolean; error?: string }> {
  const parsedSig: HLSignature = splitSignature(signature);

  const action = {
    type: "approveBuilderFee",
    hyperliquidChain: isMainnet ? "Mainnet" : "Testnet",
    signatureChainId: "0xa4b1", // Arbitrum chain ID in hex
    maxFeeRate: `${(builder.feeRate / 100).toFixed(1)}%`,
    builder: builder.address,
    nonce,
  };

  try {
    const baseUrl = isMainnet ? HL_MAINNET_API : HL_TESTNET_API;
    const response = await fetch(`${baseUrl}/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action,
        nonce,
        signature: parsedSig,
      }),
    });

    const result = (await response.json()) as {
      status?: string;
      response?: { error?: string };
    };

    if (result.status === "ok") {
      return { success: true };
    }

    return {
      success: false,
      error: result.response?.error ?? "Failed to approve builder fee",
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Network error",
    };
  }
}

// ============================================================================
// Agent Approval (API Wallet)
// ============================================================================

/**
 * Build the EIP-712 typed data for agent (API wallet) approval.
 * The main wallet signs this to authorize the API wallet to trade.
 */
export function getAgentApprovalTypedData(
  agentAddress: `0x${string}`,
  agentName: string,
  nonce: number,
  isMainnet = true,
) {
  return {
    domain: HL_EIP712_DOMAIN,
    types: {
      "HyperliquidTransaction:ApproveAgent": [
        { name: "hyperliquidChain", type: "string" },
        { name: "agentAddress", type: "address" },
        { name: "agentName", type: "string" },
        { name: "nonce", type: "uint64" },
      ],
    },
    primaryType: "HyperliquidTransaction:ApproveAgent" as const,
    message: {
      hyperliquidChain: isMainnet ? "Mainnet" : "Testnet",
      agentAddress: agentAddress.toLowerCase() as `0x${string}`,
      agentName,
      nonce: BigInt(nonce),
    },
  };
}

/**
 * Submit a signed agent approval to Hyperliquid.
 */
export async function submitAgentApproval(
  signature: `0x${string}`,
  agentAddress: `0x${string}`,
  agentName: string,
  nonce: number,
  isMainnet = true,
): Promise<{ success: boolean; error?: string }> {
  const parsedSig: HLSignature = splitSignature(signature);

  const action = {
    type: "approveAgent",
    signatureChainId: "0xa4b1",
    hyperliquidChain: isMainnet ? "Mainnet" : "Testnet",
    agentAddress: agentAddress.toLowerCase(),
    agentName,
    nonce,
  };

  try {
    const baseUrl = isMainnet ? HL_MAINNET_API : HL_TESTNET_API;
    const response = await fetch(`${baseUrl}/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action,
        nonce,
        signature: parsedSig,
      }),
    });

    const result = (await response.json()) as {
      status?: string;
      response?: { error?: string };
    };

    if (result.status === "ok") {
      return { success: true };
    }

    return {
      success: false,
      error: result.response?.error ?? "Failed to approve agent",
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Network error",
    };
  }
}
